import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { environment } from '../../enviroments/environment';

export interface UserInfo {
  sub: string;
  email: string;
  name?: string;
  given_name?: string;
  family_name?: string;
  picture?: string;
  'cognito:username'?: string;
  'cognito:groups'?: string[];
  // Campos alternativos para imagen de perfil
  profile?: string;
  avatar?: string;
  photo?: string;
  image?: string;
  identities?: any[];
}

@Injectable({
  providedIn: 'root'
})
export class UserInfoService {
  private userInfoSubject = new BehaviorSubject<UserInfo | null>(null);
  public userInfo$: Observable<UserInfo | null> = this.userInfoSubject.asObservable();

  constructor() {
    this.loadUserInfo();
  }

  /**
   * Decodificar token JWT para obtener información del usuario
   */
  private decodeToken(token: string): any {
    try {
      const base64Url = token.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split('')
          .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
          .join('')
      );
      return JSON.parse(jsonPayload);
    } catch (error) {
      console.error('Error decodificando token:', error);
      return null;
    }
  }

  /**
   * Cargar información del usuario desde el id_token
   */
  loadUserInfo(): void {
    const token = localStorage.getItem('id_token');
    if (!token) {
      this.userInfoSubject.next(null);
      return;
    }

    const decoded = this.decodeToken(token);
    if (decoded) {
      // Log para debug - ver qué campos vienen en el token
      console.log('Token decoded - campos disponibles:', Object.keys(decoded));
      console.log('Token decoded - picture:', decoded.picture);
      console.log('Token decoded - identities:', decoded.identities);
      
      this.userInfoSubject.next(decoded as UserInfo);

      // Intentar obtener información adicional del endpoint userInfo
      this.fetchUserInfoFromEndpoint();
    } else {
      this.userInfoSubject.next(null);
    }
  }

  /**
   * Obtener información adicional del usuario desde el endpoint /oauth2/userInfo
   * Este endpoint incluye atributos del proveedor federado (como la foto de Google)
   */
  private fetchUserInfoFromEndpoint(): void {
    const accessToken = localStorage.getItem('access_token');
    if (!accessToken) {
      console.log('UserInfo: No hay access_token disponible');
      return;
    }

    // Construir URL del endpoint userInfo usando la configuración del environment
    const userInfoUrl = `https://${environment.aws.oauth.domain}/oauth2/userInfo`;
    console.log('Fetching userInfo from:', userInfoUrl);

    fetch(userInfoUrl, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${accessToken}`
      }
    })
    .then(response => {
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      return response.json();
    })
    .then(userInfoData => {
      console.log('UserInfo endpoint response:', userInfoData);
      console.log('UserInfo - picture:', userInfoData.picture);
      
      // Combinar datos del token con datos del endpoint
      const currentUserInfo = this.userInfoSubject.value;
      const enhancedUserInfo = {
        ...currentUserInfo,
        ...userInfoData,
        // Asegurar que picture se actualice si viene del endpoint
        picture: userInfoData.picture || currentUserInfo?.picture
      };

      console.log('Enhanced userInfo - picture final:', enhancedUserInfo.picture);
      this.userInfoSubject.next(enhancedUserInfo as UserInfo);
    })
    .catch(error => {
      console.error('Error fetching userInfo:', error);
      // No hacer nada, mantener los datos del token
    });
  }

  /**
   * Obtener información del usuario (snapshot)
   */
  getUserInfo(): UserInfo | null {
    return this.userInfoSubject.value;
  }

  /**
   * Obtener nombre completo del usuario
   */
  getFullName(): string {
    const userInfo = this.getUserInfo();
    if (!userInfo) return 'Usuario';

    // Prioridad: name > given_name + family_name > email
    if (userInfo.name) {
      return userInfo.name;
    }

    if (userInfo.given_name || userInfo.family_name) {
      return `${userInfo.given_name || ''} ${userInfo.family_name || ''}`.trim();
    }

    return userInfo.email || 'Usuario';
  }

  /**
   * Obtener email del usuario
   */
  getEmail(): string {
    const userInfo = this.getUserInfo();
    return userInfo?.email || '';
  }

  /**
   * Obtener URL de la imagen de perfil
   * Para Google SSO, el token incluye el campo 'picture'
   */
  getProfilePicture(): string {
    const userInfo = this.getUserInfo();

    // Verificar si hay identities array (Cognito federated identity)
    if (userInfo && 'identities' in userInfo) {
      try {
        // El campo identities puede ser un string JSON o un array
        let identitiesArray = userInfo.identities;

        // Si es string, parsearlo
        if (typeof identitiesArray === 'string') {
          identitiesArray = JSON.parse(identitiesArray);
        }

        if (Array.isArray(identitiesArray) && identitiesArray.length > 0) {
          // Buscar el identity de Google
          const googleIdentity = identitiesArray.find((id: any) =>
            id.providerName === 'Google' || id.providerType === 'Google'
          );

          if (googleIdentity) {
            // Buscar la imagen en diferentes campos posibles
            const pictureUrl = googleIdentity.picture ||
                             googleIdentity.profile_picture ||
                             googleIdentity.avatar ||
                             googleIdentity.photo;

            if (pictureUrl) {
              return pictureUrl;
            }
          }
        }
      } catch (error) {
        // Error procesando identities, continuar con otros campos
      }
    }

    // Intentar campos directos en el token
    const possibleImageFields = [
      userInfo?.picture,
      userInfo?.profile,
      userInfo?.avatar,
      userInfo?.photo,
      userInfo?.image
    ];

    for (const field of possibleImageFields) {
      if (field && typeof field === 'string' && field.trim() !== '') {
        return field;
      }
    }

    // Fallback a imagen por defecto
    return '/assets/images/profile/user-1.jpg';
  }

  /**
   * Limpiar información del usuario
   */
  clearUserInfo(): void {
    this.userInfoSubject.next(null);
  }
}
